/**
 * Application root: global providers and the client-side route tree.
 *
 * Provider nesting order matters:
 *   QueryClientProvider → AuthProvider → TooltipProvider → BrowserRouter
 * AuthProvider sits inside QueryClientProvider so its child hooks can use
 * React Query. The router must be inside AuthProvider so ProtectedRoute can
 * read auth state.
 *
 * Public routes (no auth required): /auth, /reset-password, /invite/:inviteCode, /privacy, /terms, /refund-policy
 * Protected routes (require sign-in): /dashboard, /groups/:groupId, /my-items, /account
 */
import * as Sentry from '@sentry/react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { CookieConsentBanner } from '@/components/CookieConsentBanner';
import { initPostHog } from '@/lib/posthog';
import { COOKIE_CONSENT_STORAGE_KEY } from '@/lib/constants';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import GroupView from './pages/GroupView';
import MyItems from './pages/MyItems';
import InvitePage from './pages/InvitePage';
import NotFound from './pages/NotFound';
import ResetPassword from './pages/ResetPassword';
import Account from './pages/Account';
import OnboardingWelcome from './pages/onboarding/OnboardingWelcome';
import OnboardingCreateGroup from './pages/onboarding/OnboardingCreateGroup';
import OnboardingInvite from './pages/onboarding/OnboardingInvite';
import OnboardingAddItems from './pages/onboarding/OnboardingAddItems';
import GroupMembers from './pages/GroupMembers';
import GroupActivityFeed from './pages/GroupActivityFeed';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import RefundPolicy from './pages/RefundPolicy';
import { PushNotificationProvider } from '@/components/PushNotificationProvider';
import { NetworkStatusProvider } from '@/contexts/NetworkStatusContext';
import { OfflineBanner } from '@/components/OfflineBanner';
import { useNotificationRealtime } from '@/hooks/useNotificationRealtime';
import { ForceUpdateGate } from '@/components/ForceUpdateGate';

// Singleton QueryClient — shared across the app to enable cache sharing
// between components and avoid redundant re-fetches.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
    },
  },
});

/**
 * Clears the React Query cache whenever the user signs out.
 *
 * Without this, cached data from user A remains in memory after sign-out and
 * could briefly appear if user B logs in on the same browser session.
 * Must be rendered inside both QueryClientProvider and AuthProvider so it can
 * call useQueryClient() and subscribe to Supabase auth state changes.
 */
function AuthQuerySync() {
  const qc = useQueryClient();
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        qc.clear();
      }
    });
    return () => subscription.unsubscribe();
  }, [qc]);
  return null;
}

/**
 * Subscribes to Supabase Realtime for the notifications table so the
 * bell badge and list update immediately when new notifications arrive.
 */
function NotificationRealtimeSync() {
  useNotificationRealtime();
  return null;
}

/**
 *
 * @param root0
 * @param root0.children
 */
function ConsentManager({ children }: { children: ReactNode }) {
  const [showBanner, setShowBanner] = useState<boolean>(() => {
    return !localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
  });
  const [consented, setConsented] = useState<boolean>(() => {
    return localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) === 'accepted';
  });

  // On mount, initialise PostHog if the user has previously accepted.
  useEffect(() => {
    if (localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) === 'accepted') {
      initPostHog();
    }
  }, []);

  /**
   *
   */
  function handleAccept() {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'accepted');
    initPostHog();
    setConsented(true);
    setShowBanner(false);
  }

  /**
   *
   */
  function handleDecline() {
    localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'declined');
    setShowBanner(false);
  }

  return (
    <>
      {consented && (
        <>
          <Analytics />
          <SpeedInsights />
        </>
      )}
      {children}
      {showBanner && <CookieConsentBanner onAccept={handleAccept} onDecline={handleDecline} />}
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <AuthQuerySync />
      <NotificationRealtimeSync />
      <NetworkStatusProvider>
        <TooltipProvider>
          <Sonner />
          <Sentry.ErrorBoundary
            fallback={
              <p className="p-8 text-center text-destructive">
                Something went wrong. Please refresh the page.
              </p>
            }
          >
            <ForceUpdateGate>
            <ConsentManager>
              <BrowserRouter>
                <PushNotificationProvider />
                <OfflineBanner />
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/invite/:inviteCode" element={<InvitePage />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="/terms" element={<TermsConditions />} />
                  <Route path="/refund-policy" element={<RefundPolicy />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/groups/:groupId"
                    element={
                      <ProtectedRoute>
                        <GroupView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/groups/:groupId/members"
                    element={
                      <ProtectedRoute>
                        <GroupMembers />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/groups/:groupId/activity"
                    element={
                      <ProtectedRoute>
                        <GroupActivityFeed />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/my-items"
                    element={
                      <ProtectedRoute>
                        <MyItems />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account"
                    element={
                      <ProtectedRoute>
                        <Account />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/onboarding/welcome"
                    element={
                      <ProtectedRoute>
                        <OnboardingWelcome />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/onboarding/create-group"
                    element={
                      <ProtectedRoute>
                        <OnboardingCreateGroup />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/onboarding/invite"
                    element={
                      <ProtectedRoute>
                        <OnboardingInvite />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/onboarding/add-items"
                    element={
                      <ProtectedRoute>
                        <OnboardingAddItems />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/my-account" element={<Navigate to="/account" replace />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </ConsentManager>
            </ForceUpdateGate>
          </Sentry.ErrorBoundary>
        </TooltipProvider>
      </NetworkStatusProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
